import React from 'react';
import {
  ChevronRight,
  ChevronDown,
  Settings,
  ListTodo,
  Wrench,
  CheckCircle,
  Eye,
  Brain,
  FileOutput,
  GitMerge,
  Circle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import type { TraceItem, TraceItemType } from '@/types/trace';
import { formatDuration, formatTokens } from '@/utils/traceTransformer';

const iconMap: Record<TraceItemType, React.ElementType> = {
  setup: Settings,
  task: ListTodo,
  tool_call: Wrench,
  tool_result: CheckCircle,
  observation: Eye,
  thinking: Brain,
  final_output: FileOutput,
  synthesis: GitMerge,
};

const colorMap: Record<TraceItemType, string> = {
  setup: 'text-slate-500',
  task: 'text-blue-500',
  tool_call: 'text-amber-500',
  tool_result: 'text-green-500',
  observation: 'text-purple-500',
  thinking: 'text-pink-500',
  final_output: 'text-emerald-500',
  synthesis: 'text-cyan-500',
};

interface TraceTreeItemProps {
  item: TraceItem;
  depth?: number;
}

export function TraceTreeItem({ item, depth = 0 }: TraceTreeItemProps) {
  const {
    state,
    toggleNodeExpansion,
    selectTrace,
    isNodeExpanded,
    isNodeSelected,
    isNodeHighlighted,
  } = useTraceSidebar();

  const hasChildren = item.children.length > 0;
  const isExpanded = isNodeExpanded(item.id);
  const isSelected = isNodeSelected(item.id);
  const isHighlighted = isNodeHighlighted(item.id);

  const Icon = iconMap[item.type] || Circle;
  const iconColor = colorMap[item.type] || 'text-gray-500';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectTrace(item.id);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleNodeExpansion(item.id);
  };

  const statusColor = {
    pending: 'bg-gray-400',
    running: 'bg-blue-400 animate-pulse',
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
  }[item.status];

  return (
    <TooltipProvider>
      <div className="select-none">
        <Collapsible open={isExpanded} onOpenChange={() => toggleNodeExpansion(item.id)}>
          <div
            className={cn(
              'flex items-center gap-1 py-1.5 px-2 cursor-pointer rounded-md transition-colors',
              'hover:bg-accent',
              isSelected && 'bg-accent',
              isHighlighted && !isSelected && 'bg-accent/50'
            )}
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
            onClick={handleClick}
          >
            {/* Expand/Collapse Toggle */}
            {hasChildren ? (
              <CollapsibleTrigger asChild>
                <button
                  className="p-0.5 hover:bg-muted rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Collapsible's onOpenChange will handle the toggle
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
            ) : (
              <span className="w-4" />
            )}

            {/* Status Indicator */}
            <span className={cn('w-2 h-2 rounded-full flex-shrink-0', statusColor)} />

            {/* Icon */}
            <Icon className={cn('h-4 w-4 flex-shrink-0', iconColor)} />

            {/* Name */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex-1 text-sm truncate">{item.displayName}</span>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{item.displayName}</p>
                {item.toolName && <p className="text-muted-foreground">Tool: {item.toolName}</p>}
              </TooltipContent>
            </Tooltip>

            {/* Metrics */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {state.displaySettings.showLatency && item.duration !== null && (
                <span className="text-xs text-muted-foreground">
                  {formatDuration(item.duration)}
                </span>
              )}

              {state.displaySettings.showTokens && item.tokenCount.total > 0 && (
                <Badge variant="outline" className="text-xs py-0 h-5">
                  {formatTokens(item.tokenCount.total)}
                </Badge>
              )}

              {item.isCacheHit && (
                <Badge variant="secondary" className="text-xs py-0 h-5">
                  cached
                </Badge>
              )}
            </div>
          </div>

          {/* Children */}
          {hasChildren && (
            <CollapsibleContent>
              {item.children.map((child) => (
                <TraceTreeItem key={child.id} item={child} depth={depth + 1} />
              ))}
            </CollapsibleContent>
          )}
        </Collapsible>
      </div>
    </TooltipProvider>
  );
}

export default TraceTreeItem;
